import Store from 'store/store'
import { readable, derived, writable } from 'utils/state'
import { Component } from 'utils/jsx'
import microdefer from 'utils/microdefer'
import domParents from 'utils/dom-parents'

import Checkbox from 'components/dom/Checkbox'
import Range from 'components/dom/Range'
import { map } from 'missing-math'

// SEE controllers/templates for the layout/procgen part
import render from 'controllers/templates/render'

export default class Paper extends Component {
  beforeRender (props) {
    this.handleClick = this.handleClick.bind(this)
    this.leaveFullscreen = this.leaveFullscreen.bind(this)
    this.transformLandscape = this.transformLandscape.bind(this)
    this.triggerRender = this.triggerRender.bind(this)
    this.update = microdefer(this.update, this)

    this.state = {
      // For now, the template is globally registered in the Store, via pages.js
      // In a later stage, we may introduce per-paper template choice: if so,
      // the template registration will be done here
      template: Store.poster.template,

      hidden: writable(true),
      rendered: writable(false),
      rendering: writable(false),
      fullscreen: writable(false),

      h3FullWidth: writable(false),
      hasNoH3: derived(Store.text.h3, () => !Store.text.h3.current),

      showLogos: writable(true),
      fontScale: writable(undefined),
      xoff: writable(1),
      yoff: writable(1),

      canUseCMYK: readable(props.format.extension === 'png'),
      useCMYK: writable(false) // CMYK is opt-in
    }

    this.state.bleed = derived(this.state.useCMYK, cmyk => cmyk ? Store.constants.current.BLEED : 0)
    this.state.colorSpace = derived(this.state.useCMYK, cmyk => cmyk ? 'cmyk' : 'rgb')
  }

  template (props) {
    const [width, height, unit] = props.format.dimensions

    return (
      <div
        class='paper'
        store-hidden={this.state.hidden}
        store-data-color-space={this.state.colorSpace}
        store-class-is-fullscreen={this.state.fullscreen}
        store-class-is-rendering={this.state.rendering}
        store-class-is-rendered={this.state.rendered}
        event-click={this.handleClick}
      >
        <svg
          ref={this.ref('svg')}
          width={`${width}${unit}`}
          height={`${height}${unit}`}
          viewBox={`0 0 ${width} ${height}`}
        />
        <div class='paper__infos' ref={this.ref('infos')}>
          <span class='paper__info' title={props.name}>{props.name}</span>
          <span class='paper__info'>{width}×{height}{unit}</span>
        </div>
        <div class='paper__settings'>
          <div class='flex-group'>
            <Checkbox
              store-checked={this.state.useCMYK}
              store-hidden={this.state.canUseCMYK}
            >
              cmjn (imprimeur)
            </Checkbox>

            <Checkbox
              store-checked={this.state.showLogos}
              store-hidden={Store.poster.hasNoLogos}
            >
              logos
            </Checkbox>

            {(
              window.ENV.features.canToggleH3FullWidth &&
                <Checkbox
                  store-checked={this.state.h3FullWidth}
                  store-hidden={this.state.hasNoH3}
                >
                  paragraphe pleine largeur
                </Checkbox>
            )}
          </div>

          <Range
            showGhost
            fineTuneButtons
            ref={this.ref('fontScale')}
            store-value={this.state.fontScale}
            store-hidden={Store.poster.hasNoTexts}
            icon='text-height'
            debounce={200}
            min={Store.constants.current.MIN_FONT_SIZE}
            max={Store.constants.current.MAX_FONT_SIZE}
            step={1}
          />

          <Range
            fineTuneButtons
            icon='arrows-alt-h'
            ref={this.ref('horizontalOffset')}
            store-value={this.state.xoff}
            min={0}
            max={1}
            step={0.01}
          />

          <Range
            fineTuneButtons
            icon='arrows-alt-v'
            store-value={this.state.yoff}
            min={0}
            max={2}
            step={0.01}
          />
        </div>
      </div>
    )
  }

  afterRender () {
    Store.steps.index.subscribe(this.leaveFullscreen)

    this.state.xoff.subscribe(this.transformLandscape)
    this.state.yoff.subscribe(this.transformLandscape)

    this.state.hidden.subscribe(this.update)
    this.state.rendered.subscribe(this.update)

    this.state.colorSpace.subscribe(this.triggerRender)
    this.state.fontScale.subscribe(this.triggerRender)
    this.state.showLogos.subscribe(this.triggerRender)
    this.state.h3FullWidth.subscribe(this.triggerRender)

    // Reset user fontScale when triggering showLogos, to allow drapeau recalc
    this.state.showLogos.subscribe(s => this.state.fontScale.set(undefined))

    Store.text.h1.subscribe(this.triggerRender)
    Store.text.h2.subscribe(this.triggerRender)
    Store.text.h3.subscribe(this.triggerRender)
    Store.text.h4.subscribe(this.triggerRender)
    Store.text.h5.subscribe(this.triggerRender)
    Store.text.h6.subscribe(this.triggerRender)
    Store.regions.subscribe(this.triggerRender)
    Store.logos.subscribe(this.triggerRender)
    Store.cover.subscribe(this.triggerRender)
    Store.landscape.seed.subscribe(this.triggerRender)
    Store.landscape.abstraction.subscribe(this.triggerRender)
  }

  afterMount (props) {
    Store.papers.update(map => map.set(props.name, this), true)
  }

  clear () {
    this.refs.svg.innerHTML = ''
  }

  triggerRender () {
    this.state.rendered.set(false)
  }

  async update () {
    if (!this.mounted) return
    if (this._parent.state.hidden.get()) return
    if (this.state.hidden.get()) return
    if (this.state.rendered.get()) return
    if (this.state.rendering.get()) return

    this.state.rendering.set(true)
    Store.app.papersBeingRendered.update(set => { set.add(this); return set }, true)
    await render(this)

    this.state.rendering.set(false)
    Store.app.papersBeingRendered.update(set => { set.delete(this); return set }, true)

    this.state.rendered.set(true)
  }

  leaveFullscreen () {
    this.state.fullscreen.set(false)
  }

  handleClick (e) {
    if (this.state.fullscreen.current) {
      this.state.fullscreen.set(false)
    } else if (e.target && domParents(e.target).includes(this.refs.svg)) {
      this.state.fullscreen.set(!this.state.fullscreen.current)
    }
  }

  transformLandscape () {
    if (!this.refs.landscape) return
    if (!this.refs.landscapePlaceholder) return
    const xoff = this.state.xoff.get()
    const yoff = this.state.yoff.get()
    const width = +this.refs.landscape.getAttribute('data-width')
    const bleed = this.state.bleed.get()
    const translate = [
      map(xoff, 0, 1, this.props.format.dimensions[0] - (width - bleed * 2), -bleed),
      map(yoff, 0, 1, this.props.format.dimensions[1], -bleed)
    ]

    const transform = `translate(${translate.map(v => v.toFixed(3)).join(' ')})`

    this.refs.landscape.setAttribute('transform', transform)
    this.refs.landscapePlaceholder.setAttribute('transform', transform)
  }

  async toSVGString (callbacks = []) {
    // Working on a cloned node so that we can mutate and remove elements
    const svg = this.refs.svg.cloneNode(true)

    // Remove OOB cells
    const landscape = svg.querySelector('g.landscape')
    if (landscape) {
      const width = this.props.format.dimensions[0]
      const height = this.props.format.dimensions[1]
      const translate = (landscape.getAttribute('transform') || '').match(/translate\(([-\d.]+)[\s,]+([-\d.]+)\)/)
      const xoff = translate ? +translate[1] : 0
      const yoff = translate ? +translate[2] : 0
      const biggestSize = +(landscape.getAttribute('data-biggest-cell-size') || 0)
      const margin = biggestSize + this.state.bleed.get()
      for (const cell of landscape.querySelectorAll('use')) {
        const x = +cell.getAttribute('x') + xoff
        const y = +cell.getAttribute('y') + yoff
        if (x + margin > 0 && y + margin > 0 && x - margin < width && y - margin < height) continue
        cell.remove()
      }
    }

    // Remove unwanted elements
    svg.querySelectorAll('.no-export').forEach(el => el.remove())

    // Run optional callbacks against the cloned SVG
    for (const callback of [].concat(callbacks).filter(Boolean)) {
      await callback(svg)
    }

    return svg.outerHTML.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
  }
}



// WEBPACK FOOTER //
// ./src/components/dom/Paper.js